import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  StyleSheet,
  Button,
  Alert,
} from "react-native";
import LhDatePicker from "../../../../components/LhDatePicker";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LhButton from "../../../../components/LhButton";
import { useTheme } from "../../../../theme";

interface AttendanceRow {
  id: number;
  date: Date;
  input: string;
  points: number;
}

interface AttendanceTrackingProps {
  styles: any;
  attendanceData?: Array<{
    Points: number;
    Date_of_Absence: string;
  }>;
  onAttendanceChange?: (
    data: Array<{
      Points: number;
      Date_of_Absence: string;
    }>
  ) => void;
  isViewOnlyMode?: boolean;
}

const AttendanceTracking: React.FC<AttendanceTrackingProps> = ({
  styles,
  attendanceData,
  onAttendanceChange,
  isViewOnlyMode = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const darkInputBackground = colors.background.elevated;

  // Track which calendar is currently open (if any)
  const [openCalendarId, setOpenCalendarId] = useState<number | null>(null);
  const [rows, setRows] = React.useState<AttendanceRow[]>(() => {
    if (attendanceData && attendanceData.length > 0) {
      return attendanceData.map((item, index) => ({
        id: index,
        date: new Date(item.Date_of_Absence),
        input: item.Points ? item.Points.toString() : "",
        points: item.Points ? item.Points : 0,
      }));
    }
    return [{ id: 0, date: new Date(), input: "", points: 0 }];
  });

  const validateDecimalInput = (value: string): string => {
    // Allow empty string
    if (value === "") {
      return value;
    }

    // Remove any non-numeric characters except decimal point
    let cleaned = value.replace(/[^0-9.]/g, "");

    // Allow only one decimal point
    const decimalCount = (cleaned.match(/\./g) || []).length;
    if (decimalCount > 1) {
      // Keep only the first decimal point
      const firstDecimalIndex = cleaned.indexOf(".");
      cleaned =
        cleaned.substring(0, firstDecimalIndex + 1) +
        cleaned.substring(firstDecimalIndex + 1).replace(/\./g, "");
    }

    // Limit to 2 decimal places
    if (cleaned.includes(".")) {
      const [whole, decimal] = cleaned.split(".");
      cleaned = whole + "." + decimal.substring(0, 2);
    }

    return cleaned;
  };

  const updateParent = (updatedRows: AttendanceRow[]) => {
    if (onAttendanceChange) {
      const formattedData = updatedRows.map((row) => ({
        Points: parseFloat(row.input) || 0,
        Date_of_Absence: row.date.toISOString().slice(0, 10), // 'YYYY-MM-DD'
      }));
      onAttendanceChange(formattedData);
    }
  };

  // Create dynamic calendar styles for each row
  const getCalendarStyleForRow = (rowIndex: number) => {
    // Calculate position based on:
    // - Header height (approximately 50px)
    // - Row height (approximately 60px per row including borders/padding)
    // - Additional offset for better visual alignment
    const baseTopOffset = 100; // Base offset to account for header and first row
    const rowHeight = 65; // Estimated height per row including padding/borders
    const calculatedTop = baseTopOffset + rowIndex * rowHeight;
    
    return {
      calendarTop: calculatedTop,
      calendarLeft: 38, // Position it more to the right to align with date column
      calendarMarginTop: "-45px",
      calendarStyle: {
        position: "fixed",
        zIndex: 2147483647,
        boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
        transform: "translateZ(0)",
      },
    };
  };

  const handleInputChange = (id: number, value: string) => {
    if (isViewOnlyMode) return;
    const validatedValue = validateDecimalInput(value);
    const updatedRows = rows.map((row) =>
      row.id === id
        ? {
            ...row,
            input: validatedValue,
            points: parseFloat(validatedValue) || 0,
          }
        : row
    );
    setRows(updatedRows);
    updateParent(updatedRows);
  };

  const handleDateChange = (id: number, selectedDate?: Date) => {
    if (isViewOnlyMode) return;
    if (selectedDate === undefined) {
      return;
    } // Do nothing if no date selected
    const updatedRows = rows.map((row) =>
      row.id === id ? { ...row, date: selectedDate } : row
    );
    setRows(updatedRows);
    updateParent(updatedRows);
  };

  const handleAddRow = () => {
    if (isViewOnlyMode) return;
    const newRow = {
      id: rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 0,
      date: new Date(),
      input: "",
      points: 0,
    };
    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    updateParent(updatedRows);
  };

  const handleDeleteRow = (id: number) => {
    if (isViewOnlyMode) return;
    const updatedRows = rows.filter((row) => row.id !== id);
    setRows(updatedRows);
    updateParent(updatedRows);
  };

  return (
    <View style={{ width: "100%", flex: 1, position: "relative", zIndex: 19000, overflow: "visible" }}>
      <View
        style={{
          borderWidth: 1,
          borderColor: isDarkTheme ? colors.border.default : "#ced4da",
          borderRadius: 2,
          width: "100%",
          flex: 1,
          position: "relative",
          zIndex: 19000,
          overflow: "visible",
          backgroundColor: isDarkTheme ? colors.background.surface : "transparent",
        }}
      >
        {/* Fixed Header */}
        <View
          style={{
            flexDirection: "row",
            width: "100%",
            paddingLeft: 12,
            backgroundColor: "transparent",
            padding:2,
            // borderBottomWidth: 1,
            // borderBottomColor: "#dee2e6",
          }}
        >
          {/* <Text style={{ width: 32, fontWeight: "bold", textAlign: "center" }}>
            #
          </Text> */}
          <Text style={{ flex: 1, paddingLeft:8, textAlign: "left", color: isDarkTheme ? colors.text.primary : "#2D2D2D" }}>
            Date
          </Text>
          <Text style={{ flex: 1,paddingLeft: 8, textAlign: "left", color: isDarkTheme ? colors.text.primary : "#2D2D2D" }}>
            Points
          </Text>
          <Text style={{ width: 30, textAlign: "center" }} />
        </View>
        {/* Scrollable Container with All Content */}
        <View style={{ flex: 1, width: "100%", position: "relative", zIndex: 1, overflow: "visible" }}>
          <ScrollView
            style={{ flex: 1, width: "100%", position: "relative", zIndex: 1 }}
            contentContainerStyle={{ paddingBottom: 0 }}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled={true}
          >
            {/* Attendance Rows */}
            {rows.map((row, idx) => (
              <View
                key={row.id}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 8,
                  borderTopWidth: idx === 0 ? 0 : 1,
                  borderTopColor: isDarkTheme ? colors.border.divider : "#eee",
                  flexWrap: "wrap",
                  width: "100%",
                  position: "relative",
                  zIndex: 20000 - idx, // Reverse z-index: earlier rows get higher z-index
                  overflow: "visible",
                }}
              >
                <Text
                  style={{ width: 32, textAlign: "center", fontWeight: "bold", color: isDarkTheme ? colors.text.primary : "#2D2D2D" }}
                >
                  {idx + 1}
                </Text>
                <View
                  style={{
                    flex: 1,
                    minWidth: 120,
                    alignItems: "center",
                    position: "relative",
                    zIndex: 25000 - idx, // Even higher z-index for date picker containers, reverse order
                    overflow: "visible", // Allow calendar to display outside container bounds
                  }}
                >
                  <LhDatePicker
                    value={row.date}
                    onChange={(date) =>
                      handleDateChange(row.id, date ?? undefined)
                    }
                    placeholder="Select date"
                    error={false}
                    colorScheme={isDarkTheme ? "dark" : "light"}
                    iconColor={isDarkTheme ? colors.text.primary : "#0066CC"}
                    disabledIconColor={isDarkTheme ? colors.text.secondary : "#D0D0D0"}
                    style={{
                      ...getCalendarStyleForRow(idx),
                      pickerButtonStyle: {
                        backgroundColor: isDarkTheme
                          ? darkInputBackground
                          : "#ffffff",
                        borderColor: isDarkTheme
                          ? colors.border.default
                          : "#ced4da",
                      },
                      dateTextStyle: {
                        color: isDarkTheme ? colors.text.primary : "#2D2D2D",
                      },
                      placeholderTextStyle: {
                        color: isDarkTheme ? colors.text.primary : "#616161",
                      },
                    }}
                    disabled={isViewOnlyMode}
                  />
                </View>
                <TextInput
                  style={[
                   styles.formInput,
                   { color: isDarkTheme ? colors.text.primary : "#2D2D2D" },
                    {
                      flex: 1,
                      marginLeft: 8,
                      minWidth: 80,
                      textAlign: "center",
                      backgroundColor: isViewOnlyMode
                        ? isDarkTheme
                          ? colors.background.elevated
                          : "#f5f5f5"
                        : isDarkTheme
                        ? darkInputBackground
                        : "#ffffff",
                      borderColor: isDarkTheme ? colors.border.default : "#ced4da",
                    },
                  ]}
                  value={row.input}
                  onChangeText={(text) => handleInputChange(row.id, text)}
                  placeholder="Enter points"
                  placeholderTextColor={
                    isDarkTheme ? colors.text.secondary : "#616161"
                  }
                  keyboardType="decimal-pad"
                  editable={!isViewOnlyMode}
                />
                <View
                  style={{ width: 30, alignItems: "center", marginLeft: 8 }}
                >
                  {!isViewOnlyMode && (
                    <MaterialIcons
                      name="delete"
                      size={24}
                      color={isDarkTheme ? colors.text.primary : "#034895"}
                      onPress={() => handleDeleteRow(row.id)}
                      accessibilityLabel="Delete row"
                      style={{ padding: 4 }}
                    />
                  )}
                </View>
              </View>
            ))}

            {/* Add button inside ScrollView - scrollable */}
            {!isViewOnlyMode && (
              <View
                style={{
                  alignItems: "flex-start",
                  marginTop: 16,
                  marginBottom: 12,
                  marginLeft: 30,
                  width: "100%",
                }}
              >
                <LhButton
                  onPress={handleAddRow}
                  text="Add"
                  iconLeading="add"
                  style={{
                    width: 120,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDarkTheme
                      ? colors.background.elevated
                      : "white",
                    borderColor: isDarkTheme
                      ? colors.border.default
                      : "#034895",
                  }}
                  textStyle={{ color: isDarkTheme ? colors.text.primary : "#034895" }}
                  testID="add-attendance-row"
                />
              </View>
            )}

            {/* Total Points inside ScrollView - scrollable */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                padding: 20,
                // borderTopWidth: 2,
                // borderTopColor: "#0066cc",
                backgroundColor: isDarkTheme
                  ? colors.background.surface
                  : "#F5F9FF",
                borderTopWidth: 1,
                borderTopColor: isDarkTheme
                  ? colors.border.default
                  : "transparent",
                borderRadius: 4,
                width: "100%",
                justifyContent: "flex-start",
                marginTop: 18,
              }}
            >
              <Text
                style={{
                  fontWeight: "bold",
                  fontSize: 16,
                  color: isDarkTheme ? colors.text.primary : "#2D2D2D",
                  paddingRight: 16,
                }}
              >
                Total Points: {rows
                  .reduce((sum, row) => sum + (parseFloat(row.input) || 0), 0)
                  .toFixed(2)}
              </Text>
            </View>
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

// Utility to get API-ready attendance array (if needed elsewhere)
export const getAttendanceApiArray = (rows: AttendanceRow[]) =>
  rows.map((row) => ({
    Points: parseFloat(row.input) || 0,
    Date_of_Absence: row.date.toISOString().slice(0, 10), // 'YYYY-MM-DD'
  }));

export default AttendanceTracking;
